import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, getDoc, doc } from 'firebase/firestore';
import {
  HomeIcon,
  PrinterIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ArrowRightOnRectangleIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import { signOut } from 'firebase/auth';

const AdminLayout = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    // Check if current user is an admin
    const checkAdminStatus = async (user) => {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        navigate('/login');
        return;
      }

      try {
        const db = getFirestore();
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists() && userDoc.data().isAdmin) {
          setIsAdmin(true);
        } else {
          console.log('User is not an admin');
          setIsAdmin(false);
          navigate('/');
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      checkAdminStatus(user);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Will redirect through the useEffect
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow pt-5 overflow-y-auto bg-indigo-700">
          <div className="flex items-center flex-shrink-0 px-4">
            <span className="text-xl font-bold text-white">StoryBook Admin</span>
          </div>
          <div className="mt-5 flex-1 flex flex-col">
            <nav className="flex-1 px-2 pb-4 space-y-1">
              <NavLink
                to="/admin"
                className={({ isActive }) => 
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive 
                      ? 'bg-indigo-800 text-white' 
                      : 'text-indigo-100 hover:bg-indigo-600'
                  }`
                }
              >
                <HomeIcon className="mr-3 h-6 w-6" aria-hidden="true" />
                Dashboard
              </NavLink>

              <NavLink
                to="/admin/print-jobs"
                className={({ isActive }) => 
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive 
                      ? 'bg-indigo-800 text-white' 
                      : 'text-indigo-100 hover:bg-indigo-600'
                  }`
                }
              >
                <PrinterIcon className="mr-3 h-6 w-6" aria-hidden="true" />
                All Print Jobs
              </NavLink>

              <NavLink
                to="/admin/pending-fulfillment"
                className={({ isActive }) => 
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive 
                      ? 'bg-indigo-800 text-white' 
                      : 'text-indigo-100 hover:bg-indigo-600'
                  }`
                }
              >
                <ClipboardDocumentListIcon className="mr-3 h-6 w-6" aria-hidden="true" />
                Pending Fulfillment
              </NavLink>

              <NavLink
                to="/admin/completed-orders"
                className={({ isActive }) => 
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive 
                      ? 'bg-indigo-800 text-white' 
                      : 'text-indigo-100 hover:bg-indigo-600'
                  }`
                }
              >
                <ClipboardDocumentCheckIcon className="mr-3 h-6 w-6" aria-hidden="true" />
                Completed Orders
              </NavLink>

              <NavLink
                to="/admin/coupons"
                className={({ isActive }) => 
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive 
                      ? 'bg-indigo-800 text-white' 
                      : 'text-indigo-100 hover:bg-indigo-600'
                  }`
                }
              >
                <TicketIcon className="mr-3 h-6 w-6" aria-hidden="true" />
                Coupons
              </NavLink>

              <button
                onClick={handleSignOut}
                className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-indigo-100 hover:bg-indigo-600 w-full text-left"
              >
                <ArrowRightOnRectangleIcon className="mr-3 h-6 w-6" aria-hidden="true" />
                Sign Out
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top header */}
        <div className="flex items-center justify-between px-4 py-4 sm:px-6 lg:px-8 bg-white shadow-sm border-b border-gray-200">
          <h1 className="text-xl font-semibold text-gray-900">Admin Dashboard</h1>
          {/* Mobile menu button */}
          <div className="md:hidden">
            {/* Add mobile menu button if needed */}
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
